import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { getApiErrorMessage } from "@/lib/api";
import { Save, Info, CreditCard, FileSignature, RefreshCw, AlertTriangle, PieChart, Rocket, CheckCircle2, ExternalLink, KeyRound, Download, Copy } from "lucide-react";
import { HelpTip } from "@/components/HelpTip";
import { BackButton } from "@/components/BackButton";
import { AuditLogPanel } from "@/components/AuditLogPanel";

const PROVIDERS = [
  { value: "", label: "None (v1: copy to clipboard + mailto)" },
  { value: "smartlead", label: "Smartlead.ai" },
  { value: "instantly", label: "Instantly.ai" },
];

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [provider, setProvider] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [inboxMap, setInboxMap] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [s, u] = await Promise.all([api.get("/settings"), api.get("/users")]);
      setSettings(s.data);
      setUsers(u.data || []);
      setProvider(s.data.email_provider || "");
      setInboxMap(s.data.rep_inbox_mappings || {});
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        email_provider: provider || null,
        rep_inbox_mappings: inboxMap,
      };
      if (apiKey.trim()) payload.email_api_key = apiKey.trim();
      const { data } = await api.patch("/settings", payload);
      setSettings(data);
      setApiKey("");
      toast.success("Settings saved");
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const reps = users.filter((u) => u.role === "rep");

  return (
    <div className="flex-1 overflow-y-auto" data-testid="settings-page">
      <div className="px-8 py-6 max-w-3xl">
        <div className="mb-3">
          <BackButton />
        </div>
        <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted">
          Admin
        </div>
        <h1 className="text-2xl font-semibold tracking-tight text-ink mt-1">Settings</h1>

        <div
          className="mt-4 mb-8 border rounded-sm px-4 py-3 flex items-start gap-3"
          style={{
            backgroundColor: "rgba(34, 211, 192, 0.05)",
            borderColor: "rgba(34, 211, 192, 0.3)",
          }}
          data-testid="settings-adapter-note"
        >
          <Info size={14} className="text-cyan shrink-0 mt-0.5" />
          <div className="text-xs text-ink-secondary leading-relaxed">
            v1 outreach uses <span className="text-ink">Copy to clipboard</span> +{" "}
            <span className="text-ink">Open in mail client</span>. When you plug in
            Smartlead.ai / Instantly.ai below, the swappable{" "}
            <span className="font-mono text-cyan">EmailSender</span> adapter routes
            each rep&rsquo;s Send action through their own warmed inbox and opens/replies
            get tracked. Backend adapter is scaffolded (
            <span className="font-mono">/api/settings</span>) and stubbed with a
            TODO for the actual provider call.
          </div>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-16 border border-line rounded-sm animate-pulse bg-bg-surface" />
            ))}
          </div>
        ) : (
          <>
            <section className="border border-line rounded-sm bg-bg-surface">
              <div className="px-5 py-3 border-b border-line">
                <h2 className="text-base font-medium text-ink">Email provider</h2>
              </div>
              <div className="px-5 py-4 space-y-4">
                <label className="block">
                  <div className="text-[11px] font-mono uppercase tracking-wider text-ink-muted mb-1.5">
                    Provider
                  </div>
                  <select
                    value={provider}
                    onChange={(e) => setProvider(e.target.value)}
                    data-testid="provider-select"
                    className="w-full bg-bg border border-line rounded-sm px-3 py-2 text-sm text-ink focus:border-cyan transition-colors"
                  >
                    {PROVIDERS.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="block">
                  <div className="flex items-baseline justify-between mb-1.5">
                    <div className="text-[11px] font-mono uppercase tracking-wider text-ink-muted">
                      Provider API key
                    </div>
                    {settings?.has_email_api_key && (
                      <div
                        className="text-[11px] font-mono text-ink-muted"
                        data-testid="current-api-key-masked"
                      >
                        current: {settings.email_api_key_masked}
                      </div>
                    )}
                  </div>
                  <input
                    type="password"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder={
                      settings?.has_email_api_key
                        ? "Enter new key to replace…"
                        : "Paste API key…"
                    }
                    data-testid="api-key-input"
                    className="w-full bg-bg border border-line rounded-sm px-3 py-2 text-sm text-ink placeholder:text-ink-muted focus:border-cyan transition-colors font-mono"
                  />
                  <div className="text-[11px] text-ink-muted mt-1">
                    Key is stored server-side and never returned in plaintext.
                  </div>
                </label>
              </div>
            </section>

            <section className="mt-6 border border-line rounded-sm bg-bg-surface">
              <div className="px-5 py-3 border-b border-line">
                <h2 className="text-base font-medium text-ink">
                  Per-rep sending inbox
                </h2>
                <p className="text-xs text-ink-muted mt-1">
                  Map each rep to their warmed inbox. Used when a provider is
                  active to route outbound through the correct sender.
                </p>
              </div>
              <div className="px-5 py-4">
                {reps.length === 0 ? (
                  <div className="text-sm text-ink-muted">No reps yet.</div>
                ) : (
                  <div className="divide-y divide-line" data-testid="rep-inbox-list">
                    {reps.map((r) => (
                      <div key={r.id} className="py-3 flex items-center gap-4">
                        <div className="min-w-[160px]">
                          <div className="text-sm text-ink">{r.name}</div>
                          <div className="text-[11px] text-ink-muted font-mono">
                            {r.email}
                          </div>
                        </div>
                        <input
                          type="email"
                          value={inboxMap[r.id] || ""}
                          onChange={(e) =>
                            setInboxMap({ ...inboxMap, [r.id]: e.target.value })
                          }
                          placeholder="warmed-inbox@getmainstreet.co"
                          data-testid={`inbox-input-${r.id}`}
                          className="flex-1 bg-bg border border-line rounded-sm px-3 py-1.5 text-sm text-ink placeholder:text-ink-muted focus:border-cyan transition-colors font-mono"
                        />
                        {!r.active && (
                          <span className="text-[10px] font-mono uppercase text-ink-muted tracking-wider">
                            deactivated
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>

            <div className="mt-6 flex items-center gap-3">
              <button
                onClick={save}
                disabled={saving}
                data-testid="save-settings-btn"
                className="flex items-center gap-2 bg-cyan text-bg font-medium py-2 px-4 rounded-sm hover:bg-cyan-hover transition-colors disabled:opacity-60 active:scale-[0.98]"
              >
                <Save size={13} />
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>

            <StripeSection settings={settings} onSaved={load} />
            <GoLivePanel />
            <IntakeTokensPanel />
            <CommissionsSection />
            <AgreementSection settings={settings} onSaved={load} />
            <AuditLogPanel />
          </>
        )}
      </div>
    </div>
  );
}

function StripeSection({ settings, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [secret, setSecret] = useState("");
  const [pub, setPub] = useState(settings?.stripe_publishable_key || "");
  const [webhook, setWebhook] = useState("");
  const [mode, setMode] = useState(settings?.stripe_mode || "test");

  useEffect(() => {
    setPub(settings?.stripe_publishable_key || "");
    setMode(settings?.stripe_mode || "test");
  }, [settings]);

  const webhookEndpoint = `${process.env.REACT_APP_BACKEND_URL}/api/stripe/webhook`;
  const usingEmergent = !!settings?.stripe_using_env;

  const save = async () => {
    setSaving(true);
    try {
      const payload = { stripe_mode: mode, stripe_publishable_key: pub };
      if (secret.trim()) payload.stripe_secret_key = secret.trim();
      if (webhook.trim()) payload.stripe_webhook_secret = webhook.trim();
      await api.patch("/settings/stripe", payload);
      setSecret("");
      setWebhook("");
      toast.success("Stripe keys saved");
      await onSaved();
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/settings/stripe/sync");
      toast.success(`Synced ${data.synced_prices} prices to Stripe`);
      await onSaved();
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setSyncing(false);
    }
  };

  const synced = !!settings?.stripe_synced_at;

  return (
    <section className="mt-8 border border-line rounded-sm bg-bg-surface" data-testid="stripe-settings-section">
      <div className="px-5 py-3 border-b border-line">
        <h2 className="text-base font-medium text-ink flex items-center gap-2">
          <CreditCard size={14} className="text-cyan" />
          Payments (Stripe)
        </h2>
        {usingEmergent ? (
          <p className="text-xs text-ink-muted mt-1">
            Using the <span className="text-cyan">Emergent-managed Stripe sandbox</span>.
            Products, prices, and the webhook are pre-wired for you. Claim the account
            when you&rsquo;re ready to go live — reliability first.
          </p>
        ) : (
          <p className="text-xs text-ink-muted mt-1">
            Paste your OWN Stripe keys (test or live). Nothing is hardcoded. Keys are
            stored server-side and only returned masked.
          </p>
        )}
      </div>

      {usingEmergent && (
        <div className="px-5 py-4 border-b border-line" data-testid="stripe-emergent-panel">
          <div className="grid md:grid-cols-4 gap-3 mb-4">
            <StatTile label="Connector" value="Emergent-managed" testId="stripe-connector-tile" />
            <StatTile label="Mode" value={settings?.stripe_mode?.toUpperCase() || "TEST"} testId="stripe-mode-tile" />
            <StatTile
              label="Catalog"
              value={
                synced
                  ? `${Object.keys(settings.stripe_price_ids || {}).length} prices synced`
                  : "Not synced"
              }
              testId="stripe-catalog-tile"
            />
            <StatTile
              label="Sandbox expires"
              value={
                settings?.stripe_sandbox_expires_at
                  ? new Date(settings.stripe_sandbox_expires_at).toLocaleDateString()
                  : "—"
              }
              testId="stripe-expires-tile"
            />
          </div>
          {settings?.stripe_onboarding_url && (
            <a
              href={settings.stripe_onboarding_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="stripe-onboarding-link"
              className="inline-flex items-center gap-2 bg-cyan text-bg font-medium py-2 px-4 rounded-sm hover:bg-cyan-hover transition-colors"
            >
              <ExternalIcon />
              Claim this Stripe account → activate live payments
            </a>
          )}
          <div className="mt-4 flex items-center gap-3">
            <button
              onClick={sync}
              disabled={syncing}
              data-testid="sync-stripe-btn"
              className="flex items-center gap-2 border border-line text-ink hover:bg-bg-hover py-2 px-4 rounded-sm transition-colors disabled:opacity-40"
            >
              <RefreshCw size={13} className={syncing ? "animate-spin" : ""} />
              {syncing ? "Syncing…" : "Re-sync catalog"}
            </button>
            {synced && (
              <span className="text-[11px] text-ink-muted" data-testid="stripe-synced-at">
                Last sync: {new Date(settings.stripe_synced_at).toLocaleString()}
              </span>
            )}
          </div>
        </div>
      )}

      <div className="px-5 py-4 space-y-4">
        <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted">
          {usingEmergent ? "Override — paste your own keys (advanced)" : "Configure keys"}
        </div>

        <label className="block">
          <div className="text-[11px] font-mono uppercase tracking-wider text-ink-muted mb-1.5">
            Mode
          </div>
          <div className="inline-flex border border-line rounded-sm overflow-hidden" data-testid="stripe-mode-toggle">
            {["test", "live"].map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                data-testid={`stripe-mode-${m}`}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider transition-colors ${
                  mode === m
                    ? m === "live"
                      ? "bg-star/20 text-star border border-star/40"
                      : "bg-cyan-muted text-ink"
                    : "text-ink-secondary hover:text-ink hover:bg-bg-hover"
                }`}
              >
                {m}
              </button>
            ))}
          </div>
        </label>

        <label className="block">
          <div className="flex items-baseline justify-between mb-1.5">
            <div className="text-[11px] font-mono uppercase tracking-wider text-ink-muted">
              Secret key
            </div>
            {settings?.has_stripe_secret_key && (
              <div className="text-[11px] font-mono text-ink-muted" data-testid="stripe-secret-masked">
                {usingEmergent ? "sandbox: " : "current: "}{settings.stripe_secret_key_masked}
              </div>
            )}
          </div>
          <input
            type="password"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            placeholder={usingEmergent ? "Paste your own key to override…" : (settings?.has_stripe_secret_key ? "Enter new key to replace…" : "sk_test_… or sk_live_…")}
            data-testid="stripe-secret-input"
            className="w-full bg-bg border border-line rounded-sm px-3 py-2 text-sm text-ink placeholder:text-ink-muted focus:border-cyan transition-colors font-mono"
          />
        </label>

        <label className="block">
          <div className="text-[11px] font-mono uppercase tracking-wider text-ink-muted mb-1.5">
            Publishable key
          </div>
          <input
            value={pub}
            onChange={(e) => setPub(e.target.value)}
            placeholder="pk_test_… or pk_live_…"
            data-testid="stripe-pub-input"
            className="w-full bg-bg border border-line rounded-sm px-3 py-2 text-sm text-ink placeholder:text-ink-muted focus:border-cyan transition-colors font-mono"
          />
        </label>

        <label className="block">
          <div className="flex items-baseline justify-between mb-1.5">
            <div className="text-[11px] font-mono uppercase tracking-wider text-ink-muted">
              Webhook signing secret
            </div>
            {settings?.has_stripe_webhook_secret && (
              <div className="text-[11px] font-mono text-ink-muted">
                {usingEmergent ? "sandbox: " : "current: "}{settings.stripe_webhook_secret_masked}
              </div>
            )}
          </div>
          <input
            type="password"
            value={webhook}
            onChange={(e) => setWebhook(e.target.value)}
            placeholder={usingEmergent ? "(preview webhook pre-wired — override if needed)" : (settings?.has_stripe_webhook_secret ? "Enter new secret to replace…" : "whsec_…")}
            data-testid="stripe-webhook-input"
            className="w-full bg-bg border border-line rounded-sm px-3 py-2 text-sm text-ink placeholder:text-ink-muted focus:border-cyan transition-colors font-mono"
          />
          <div className="text-[11px] text-ink-muted mt-1.5 leading-relaxed">
            {usingEmergent
              ? "The Emergent-managed sandbox webhook is already pointed at this backend and configured. Only override this when you go live with your own account."
              : "In Stripe Dashboard → Developers → Webhooks, add endpoint"}
            {!usingEmergent && (
              <>
                <br />
                <span className="text-cyan font-mono select-all" data-testid="stripe-webhook-endpoint">
                  {webhookEndpoint}
                </span>
                <br />
                Listen for <span className="font-mono">checkout.session.completed</span>,
                <span className="font-mono"> async_payment_succeeded</span>,
                <span className="font-mono"> async_payment_failed</span>,
                <span className="font-mono"> checkout.session.expired</span>. Paste the
                signing secret above.
              </>
            )}
          </div>
        </label>

        <div className="flex items-center gap-3">
          <button
            onClick={save}
            disabled={saving}
            data-testid="save-stripe-btn"
            className="flex items-center gap-2 bg-cyan text-bg font-medium py-2 px-4 rounded-sm hover:bg-cyan-hover transition-colors disabled:opacity-60"
          >
            <Save size={13} />
            {saving ? "Saving…" : usingEmergent ? "Save override" : "Save Stripe keys"}
          </button>
          {!usingEmergent && (
            <button
              onClick={sync}
              disabled={syncing || !settings?.has_stripe_secret_key}
              data-testid="sync-stripe-byok-btn"
              className="flex items-center gap-2 border border-line text-ink hover:bg-bg-hover py-2 px-4 rounded-sm transition-colors disabled:opacity-40"
            >
              <RefreshCw size={13} className={syncing ? "animate-spin" : ""} />
              {syncing ? "Syncing…" : synced ? "Re-sync catalog" : "Sync catalog to Stripe"}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

function StatTile({ label, value, testId }) {
  return (
    <div className="border border-line rounded-sm bg-bg p-3" data-testid={testId}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
        {label}
      </div>
      <div className="text-sm text-ink font-medium mt-1">{value}</div>
    </div>
  );
}

function ExternalIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

// -----------------------------------------------------------------------------
// GoLivePanel — surfaces the exact step needed to activate live Stripe payments
// in the CURRENT environment. Reads /api/settings/stripe/status.
// -----------------------------------------------------------------------------

const NEXT_ACTION_META = {
  unconfigured: {
    color: "#8FA5AA",
    title: "Stripe not configured",
    icon: AlertTriangle,
  },
  complete_onboarding: {
    color: "#F4B860",
    title: "Complete KYC to unlock live payments",
    icon: AlertTriangle,
  },
  redeploy: {
    color: "#F4B860",
    title: "Redeploy to switch to LIVE keys",
    icon: Rocket,
  },
  sync_catalog: {
    color: "#22D3C0",
    title: "Sync catalog to live account",
    icon: RefreshCw,
  },
  all_set: {
    color: "#22D3C0",
    title: "Live payments active",
    icon: CheckCircle2,
  },
  error: {
    color: "#F87171",
    title: "Stripe error",
    icon: AlertTriangle,
  },
};

function GoLivePanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = async (soft = false) => {
    if (!soft) setLoading(true); else setRefreshing(true);
    try {
      const { data } = await api.get("/settings/stripe/status");
      setStatus(data);
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const syncCatalog = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/settings/stripe/sync");
      toast.success(`Synced ${data.synced_prices} prices to this account`);
      await load(true);
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <section className="mt-8 border border-line rounded-sm bg-bg-surface p-5">
        <div className="h-24 animate-pulse bg-bg rounded-sm" />
      </section>
    );
  }
  if (!status) return null;

  const meta = NEXT_ACTION_META[status.next_action] || NEXT_ACTION_META.error;
  const Icon = meta.icon;
  const acct = status.account || {};
  const runtime = status.runtime_mode || "unknown";
  const isLive = runtime === "live";

  return (
    <section
      className="mt-8 border border-line rounded-sm bg-bg-surface"
      data-testid="go-live-panel"
    >
      <div className="px-5 py-3 border-b border-line flex items-center gap-2">
        <Rocket size={14} className="text-cyan" />
        <h2 className="text-base font-medium text-ink">Live payments</h2>
        <HelpTip
          text="Live-payment readiness for THIS running process. Reads Stripe's account state, checks catalog sync, and tells you the ONE next action to take."
          testId="tip-go-live"
        />
        <span
          data-testid="go-live-runtime-badge"
          className={`ml-2 text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${
            isLive ? "text-cyan border-cyan" : "text-star border-star"
          }`}
        >
          {runtime === "live" ? "LIVE" : runtime === "test" ? "TEST" : "?"}
        </span>
        <button
          onClick={() => load(true)}
          disabled={refreshing}
          data-testid="go-live-refresh-btn"
          className="ml-auto flex items-center gap-2 text-xs text-ink-secondary hover:text-ink border border-line hover:bg-bg-hover px-3 py-1.5 rounded-sm transition-colors disabled:opacity-40"
        >
          <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Next-action callout */}
      <div
        className="mx-5 mt-5 border rounded-sm p-4 flex gap-3"
        style={{ borderColor: meta.color + "55", backgroundColor: meta.color + "0F" }}
        data-testid={`go-live-action-${status.next_action}`}
      >
        <Icon size={18} style={{ color: meta.color, flexShrink: 0, marginTop: 2 }} />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-ink">{meta.title}</div>
          <div className="text-xs text-ink-secondary mt-1 leading-relaxed">
            {status.next_action_message}
          </div>
          <GoLiveCTA
            action={status.next_action}
            onboardingUrl={status.onboarding_url}
            onSync={syncCatalog}
            syncing={syncing}
          />
        </div>
      </div>

      {/* Status grid */}
      <div className="grid md:grid-cols-2 gap-4 p-5">
        <div className="border border-line rounded-sm bg-bg p-4">
          <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted mb-3">
            Runtime
          </div>
          <StatusRow label="Key mode" value={runtime.toUpperCase()} valueColor={isLive ? "#22D3C0" : "#F4B860"} testId="go-live-key-mode" />
          <StatusRow label="Managed by" value={status.using_env_keys ? "Emergent (env)" : "BYOK"} />
          <StatusRow label="STRIPE_MODE env" value={status.env_mode_flag || "—"} />
          {status.sandbox_expires_at && !isLive && (
            <StatusRow label="Sandbox expires" value={new Date(status.sandbox_expires_at).toLocaleDateString()} />
          )}
        </div>
        <div className="border border-line rounded-sm bg-bg p-4">
          <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted mb-3">
            Stripe account
          </div>
          <StatusRow label="Account" value={acct.id || "—"} mono />
          <StatusRow label="Country" value={acct.country || "—"} />
          <StatusRow
            label="Charges enabled"
            value={acct.charges_enabled ? "yes" : "no"}
            valueColor={acct.charges_enabled ? "#22D3C0" : "#F4B860"}
          />
          <StatusRow
            label="KYC submitted"
            value={acct.details_submitted ? "yes" : "no"}
            valueColor={acct.details_submitted ? "#22D3C0" : "#F4B860"}
          />
          <StatusRow
            label="Payouts enabled"
            value={acct.payouts_enabled ? "yes" : "no"}
            valueColor={acct.payouts_enabled ? "#22D3C0" : "#F4B860"}
          />
        </div>
        <div className="border border-line rounded-sm bg-bg p-4">
          <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted mb-3">
            Catalog
          </div>
          <StatusRow
            label="Managed prices on account"
            value={`${status.catalog?.managed_prices_on_account ?? 0} / ${status.catalog?.expected ?? 10}`}
            valueColor={(status.catalog?.managed_prices_on_account ?? 0) >= (status.catalog?.expected ?? 10) ? "#22D3C0" : "#F4B860"}
            testId="go-live-catalog-count"
          />
          <div className="text-[11px] text-ink-muted mt-3 leading-relaxed">
            Products carry the metadata tag <span className="font-mono">managed_by=main-street</span>.
            The sync is idempotent — safe to run against live too.
          </div>
        </div>
        <div className="border border-line rounded-sm bg-bg p-4">
          <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted mb-3">
            Webhooks on this account
          </div>
          {(status.webhooks?.matching || []).length === 0 ? (
            <div className="text-xs text-ink-muted">
              No webhook endpoint registered pointing at this app on this account.
              A live-mode redeploy will register one automatically.
            </div>
          ) : (
            <div className="space-y-2">
              {status.webhooks.matching.map((w) => (
                <div key={w.id} className="text-[11px] font-mono">
                  <div className="text-ink truncate">{w.url}</div>
                  <div className="text-ink-muted">
                    {w.status} · {w.livemode ? "livemode" : "testmode"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function GoLiveCTA({ action, onboardingUrl, onSync, syncing }) {
  if (action === "complete_onboarding") {
    return (
      <div className="mt-3 flex flex-col gap-2">
        {onboardingUrl && (
          <a
            href={onboardingUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="go-live-open-onboarding-btn"
            className="inline-flex items-center gap-2 self-start bg-cyan text-bg font-medium py-2 px-4 rounded-sm hover:bg-cyan-hover transition-colors text-sm"
          >
            Open Stripe onboarding <ExternalLink size={13} />
          </a>
        )}
        <div className="text-[11px] text-ink-muted leading-relaxed">
          You&rsquo;ll enter business info, verify identity, and connect a payout bank
          account. Once approved, Emergent&rsquo;s platform auto-swaps the LIVE keys
          into your deployed app&rsquo;s environment — you&rsquo;ll then need to click
          <span className="font-mono text-ink"> Deploy </span>
          to pick them up.
        </div>
      </div>
    );
  }
  if (action === "redeploy") {
    return (
      <div className="mt-3 flex flex-col gap-2">
        <div
          className="inline-flex items-center gap-2 self-start bg-cyan text-bg font-medium py-2 px-4 rounded-sm text-sm"
          data-testid="go-live-redeploy-note"
        >
          <Rocket size={14} />
          Click <span className="font-mono">⏵ Deploy</span> in the Emergent header
        </div>
        <div className="text-[11px] text-ink-muted leading-relaxed max-w-2xl">
          Your Stripe account is claimed and KYC is complete — the platform has
          already prepared the live keys in your deployment&rsquo;s environment. The
          currently-running process still holds the sandbox keys from the last
          deploy; a fresh deploy will load the live ones. After redeploy, this
          panel will flip to <span className="text-cyan">LIVE</span> and the
          Sync-catalog action below will register the 10 products on your live account.
        </div>
      </div>
    );
  }
  if (action === "sync_catalog") {
    return (
      <div className="mt-3 flex flex-col gap-2">
        <button
          onClick={onSync}
          disabled={syncing}
          data-testid="go-live-sync-catalog-btn"
          className="inline-flex items-center gap-2 self-start bg-cyan text-bg font-medium py-2 px-4 rounded-sm hover:bg-cyan-hover transition-colors text-sm disabled:opacity-40"
        >
          <RefreshCw size={13} className={syncing ? "animate-spin" : ""} />
          {syncing ? "Syncing…" : "Sync 10 products to live account"}
        </button>
      </div>
    );
  }
  if (action === "all_set") {
    return (
      <div className="mt-3 text-[11px] text-ink-muted leading-relaxed">
        You can safely take real payments. Run a small $1 test charge on a real
        card if you want a smoke-check. The Revenue panel on the Dashboard shows
        live MRR from this account.
      </div>
    );
  }
  return null;
}

function StatusRow({ label, value, valueColor, mono, testId }) {
  return (
    <div className="flex items-baseline justify-between py-1" data-testid={testId}>
      <span className="text-xs text-ink-secondary">{label}</span>
      <span
        className={`text-xs tabular-nums ${mono ? "font-mono" : "font-medium"}`}
        style={{ color: valueColor || "#EFF4F2" }}
      >
        {value}
      </span>
    </div>
  );
}


// -----------------------------------------------------------------------------
// Intake tokens (SEC-003 fix, Option 1). Generates HMAC-signed tokens per
// lead so the "Make it yours" widgets on getmainstreet-previews.pages.dev
// can POST intake safely. Shows the widget integration snippet + a CSV
// download for the deploy pipeline.
// -----------------------------------------------------------------------------

function IntakeTokensPanel() {
  const [status, setStatus] = useState(null);
  const [ttl, setTtl] = useState(365);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/admin/intake-tokens/status");
      setStatus(data);
    } catch (e) {
      // silent
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const generate = async () => {
    setGenerating(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/intake-tokens/generate", { ttl_days: ttl });
      setResult(data);
      toast.success(`Generated ${data.count} intake tokens (valid ${data.ttl_days} days)`);
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setGenerating(false);
    }
  };

  const downloadCsv = () => {
    if (!result) return;
    const header = "place_id,business_name,token,expires_at,url\n";
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const rows = result.tokens
      .map((t) => {
        const url = `${backend}/api/intake/${encodeURIComponent(t.place_id)}?token=${t.token}`;
        const safeName = (t.business_name || "").replace(/"/g, '""');
        return `${t.place_id},"${safeName}",${t.token},${t.expires_at},"${url}"`;
      })
      .join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `intake-tokens-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const snippet = `<!-- Widget snippet — paste into the "Make it yours" template.
     Replace {{PLACE_ID}} and {{TOKEN}} with the values from the CSV export. -->
<script>
  (function() {
    var PLACE_ID = "{{PLACE_ID}}";
    var TOKEN    = "{{TOKEN}}";
    var API      = "${process.env.REACT_APP_BACKEND_URL || 'https://YOUR-DEPLOY-URL'}";
    window.mscIntakeSubmit = function(payload) {
      return fetch(API + "/api/intake/" + encodeURIComponent(PLACE_ID) + "?token=" + TOKEN, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      }).then(function(r) { return r.json(); });
    };
  })();
</script>`;

  return (
    <section
      className="mt-8 border border-line rounded-sm bg-bg-surface"
      data-testid="intake-tokens-panel"
    >
      <div className="px-5 py-3 border-b border-line flex items-center gap-2">
        <KeyRound size={14} className="text-cyan" />
        <h2 className="text-base font-medium text-ink">&ldquo;Make it yours&rdquo; widget tokens</h2>
        {status && (
          <span
            className={`ml-2 text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${
              status.required ? "text-cyan border-cyan" : "text-star border-star"
            }`}
            data-testid="intake-tokens-mode"
          >
            {status.required ? "STRICT" : "ROLLOUT"}
          </span>
        )}
      </div>

      {/* Explainer + rollout state */}
      <div className="px-5 py-4 border-b border-line">
        <p className="text-[13px] text-ink-secondary leading-relaxed max-w-3xl">
          Every &ldquo;Make it yours&rdquo; widget on the 125 sample sites needs an HMAC-signed
          token to POST intake data. Widgets embed the token at build time. Server
          verifies signature + expiry on every submit — blocks read/tamper/spam.
        </p>
        {status && !status.required && (
          <div className="mt-3 text-[12px] text-star flex items-start gap-2">
            <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
            <span>
              <b>Rollout mode</b> — legacy unsigned POSTs still accepted so live widgets
              don&rsquo;t break. Once every widget on getmainstreet-previews.pages.dev is
              rebuilt with a token, set <span className="font-mono text-ink">INTAKE_TOKEN_REQUIRED=true</span> in
              the deployment env and redeploy to enforce.
            </span>
          </div>
        )}
      </div>

      {/* Generator */}
      <div className="px-5 py-4 border-b border-line">
        <div className="flex items-end gap-3 flex-wrap">
          <label className="block">
            <div className="text-[11px] font-mono uppercase tracking-wider text-ink-muted mb-1.5">
              Token TTL (days)
            </div>
            <input
              type="number"
              min="1"
              max="3650"
              value={ttl}
              onChange={(e) => setTtl(Math.max(1, Math.min(3650, parseInt(e.target.value) || 365)))}
              className="w-28 bg-bg border border-line rounded-sm px-3 py-2 text-sm text-ink font-mono focus:border-cyan transition-colors tabular-nums"
              data-testid="intake-tokens-ttl"
            />
          </label>
          <button
            onClick={generate}
            disabled={generating}
            data-testid="intake-tokens-generate-btn"
            className="flex items-center gap-2 bg-cyan text-bg font-medium py-2 px-4 rounded-sm hover:bg-cyan-hover transition-colors disabled:opacity-40"
          >
            <KeyRound size={13} />
            {generating ? "Generating…" : "Generate tokens for all leads"}
          </button>
          {result && (
            <button
              onClick={downloadCsv}
              data-testid="intake-tokens-csv-btn"
              className="flex items-center gap-2 border border-line text-ink hover:bg-bg-hover font-medium py-2 px-3 rounded-sm transition-colors text-sm"
            >
              <Download size={13} />
              Download CSV ({result.count})
            </button>
          )}
        </div>
        {result && (
          <div className="mt-3 text-[12px] text-ink-secondary" data-testid="intake-tokens-result">
            Generated <span className="text-cyan font-mono">{result.count}</span> tokens ·
            valid <span className="font-mono">{result.ttl_days} days</span>. Download the CSV, feed
            it into the widget build pipeline, then redeploy the Cloudflare Pages project.
          </div>
        )}
      </div>

      {/* Widget snippet */}
      <div className="px-5 py-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted">
            Widget integration snippet
          </div>
          <button
            onClick={() => {
              navigator.clipboard.writeText(snippet);
              toast.success("Snippet copied");
            }}
            data-testid="intake-tokens-copy-snippet"
            className="flex items-center gap-1.5 text-xs text-ink-secondary hover:text-ink border border-line hover:bg-bg-hover px-2 py-1 rounded-sm transition-colors"
          >
            <Copy size={11} />
            Copy
          </button>
        </div>
        <pre className="bg-bg border border-line rounded-sm p-3 text-[11px] font-mono text-ink-secondary overflow-x-auto max-w-full whitespace-pre">
{snippet}
        </pre>
      </div>
    </section>
  );
}




function CommissionsSection() {
  const [rates, setRates] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/commissions");
      setRates(data.rates);
      setDefaults(data.defaults);
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const update = (bucket, key, val) => {
    const n = Number(val);
    setRates({
      ...rates,
      [bucket]: { ...rates[bucket], [key]: isNaN(n) ? 0 : n },
    });
  };

  const bucketSum = (bucket) =>
    !rates?.[bucket]
      ? 0
      : Number(rates[bucket].rep || 0) +
        Number(rates[bucket].office_manager || 0) +
        Number(rates[bucket].owner || 0);

  const setupSum = bucketSum("setup");
  const mrrSum = bucketSum("mrr");
  const valid = Math.abs(setupSum - 100) < 0.01 && Math.abs(mrrSum - 100) < 0.01;

  const save = async () => {
    if (!valid) {
      toast.error("Each bucket must sum to exactly 100%.");
      return;
    }
    setSaving(true);
    try {
      await api.put("/settings/commissions", rates);
      toast.success("Commission rates updated");
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const restoreDefaults = () => {
    if (!defaults) return;
    setRates({
      setup: { ...defaults.setup },
      mrr: { ...defaults.mrr },
    });
  };

  if (loading || !rates) {
    return (
      <section className="mt-8 border border-line rounded-sm bg-bg-surface p-5">
        <div className="h-20 animate-pulse bg-bg rounded-sm" />
      </section>
    );
  }

  return (
    <section
      className="mt-8 border border-line rounded-sm bg-bg-surface"
      data-testid="commissions-settings-section"
    >
      <div className="px-5 py-3 border-b border-line">
        <h2 className="text-base font-medium text-ink flex items-center gap-2">
          <PieChart size={14} className="text-cyan" />
          Commission rates
          <HelpTip
            text="Splits every dollar three ways: Rep, Office Manager, Owner. Rep gets credit on deals from leads assigned to them. Unassigned deals: the rep-share rolls into your (Owner) cut."
            testId="tip-commission-rates"
          />
        </h2>
        <p className="text-xs text-ink-muted mt-1 leading-relaxed">
          How each dollar is split between Rep, Office Manager, and Owner. Each
          bucket must total 100%. Changes take effect immediately across
          everyone&rsquo;s Commissions page.
        </p>
      </div>
      <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-line">
        <RateBucket
          testIdPrefix="setup"
          label="Setup fee (one-time)"
          bucket={rates.setup}
          onChange={(k, v) => update("setup", k, v)}
          sum={setupSum}
        />
        <RateBucket
          testIdPrefix="mrr"
          label="Monthly (MRR)"
          bucket={rates.mrr}
          onChange={(k, v) => update("mrr", k, v)}
          sum={mrrSum}
        />
      </div>
      <div className="px-5 py-4 border-t border-line flex items-center gap-3">
        <button
          onClick={save}
          disabled={!valid || saving}
          data-testid="save-commissions-btn"
          className="flex items-center gap-2 bg-cyan text-bg font-medium py-2 px-4 rounded-sm hover:bg-cyan-hover transition-colors disabled:opacity-40"
        >
          <Save size={13} />
          {saving ? "Saving…" : "Save rates"}
        </button>
        <button
          onClick={restoreDefaults}
          type="button"
          data-testid="restore-defaults-commissions-btn"
          className="text-xs text-ink-secondary hover:text-ink border border-line hover:bg-bg-hover px-3 py-1.5 rounded-sm transition-colors"
        >
          Restore defaults
        </button>
        {!valid && (
          <span className="text-xs text-star flex items-center gap-1.5">
            <AlertTriangle size={12} />
            Each column must sum to 100%.
          </span>
        )}
      </div>
    </section>
  );
}

function RateBucket({ label, bucket, onChange, sum, testIdPrefix }) {
  const roles = [
    { key: "rep", label: "Rep" },
    { key: "office_manager", label: "Office Manager" },
    { key: "owner", label: "Owner" },
  ];
  const valid = Math.abs(sum - 100) < 0.01;
  return (
    <div className="p-5">
      <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted mb-3">
        {label}
      </div>
      <div className="space-y-3">
        {roles.map((r) => (
          <label key={r.key} className="flex items-center gap-3">
            <span className="text-sm text-ink w-36">{r.label}</span>
            <input
              type="number"
              min="0"
              max="100"
              step="1"
              value={bucket[r.key] ?? 0}
              onChange={(e) => onChange(r.key, e.target.value)}
              data-testid={`commission-input-${testIdPrefix}-${r.key}`}
              className="w-24 bg-bg border border-line rounded-sm px-2 py-1.5 text-sm text-ink font-mono focus:border-cyan transition-colors tabular-nums"
            />
            <span className="text-sm text-ink-muted">%</span>
          </label>
        ))}
      </div>
      <div className="mt-4 flex items-center justify-between text-[11px] font-mono uppercase tracking-widest">
        <span className="text-ink-muted">Total</span>
        <span
          className={valid ? "text-cyan" : "text-star"}
          data-testid={`commission-sum-${testIdPrefix}`}
        >
          {sum.toFixed(0)}%
        </span>
      </div>
    </div>
  );
}

function AgreementSection({ settings, onSaved }) {
  const [template, setTemplate] = useState(settings?.agreement_template || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setTemplate(settings?.agreement_template || "");
  }, [settings]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/settings/agreement", { agreement_template: template });
      toast.success("Agreement template saved");
      await onSaved();
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mt-8 mb-16 border border-line rounded-sm bg-bg-surface" data-testid="agreement-settings-section">
      <div className="px-5 py-3 border-b border-line">
        <h2 className="text-base font-medium text-ink flex items-center gap-2">
          <FileSignature size={14} className="text-cyan" />
          Service Agreement template
        </h2>
        <p className="text-xs text-ink-muted mt-1 leading-relaxed">
          HTML with merge fields. Available:{" "}
          <span className="font-mono text-cyan">{"{business_name}"}</span>,{" "}
          <span className="font-mono text-cyan">{"{setup_total}"}</span>,{" "}
          <span className="font-mono text-cyan">{"{monthly_total}"}</span>,{" "}
          <span className="font-mono text-cyan">{"{addons}"}</span>,{" "}
          <span className="font-mono text-cyan">{"{date}"}</span>. Seeded with the SOP
          Jason provided — edit as needed.
        </p>
      </div>
      <div className="px-5 py-4">
        <textarea
          value={template}
          onChange={(e) => setTemplate(e.target.value)}
          rows={16}
          spellCheck={false}
          data-testid="agreement-template-input"
          className="w-full bg-bg border border-line rounded-sm px-3 py-3 text-xs text-ink font-mono focus:border-cyan transition-colors resize-y leading-relaxed"
        />
        <div className="mt-3 flex items-center gap-3">
          <button
            onClick={save}
            disabled={saving}
            data-testid="save-agreement-btn"
            className="flex items-center gap-2 bg-cyan text-bg font-medium py-2 px-4 rounded-sm hover:bg-cyan-hover transition-colors disabled:opacity-60"
          >
            <Save size={13} />
            {saving ? "Saving…" : "Save agreement"}
          </button>
        </div>
      </div>
    </section>
  );
}
